import React, { createContext, useContext } from 'react';

/**
 * @typedef {import('../../types').ProductDTO} ProductDTO
 * @typedef {import('../../types').ShippingAddressDTO} ShippingAddressDTO
 * @typedef {import('../../types').ProfileData} ProfileData
 * @typedef {import('../../types').AdjustedPriceData} AdjustedPriceData
 * @typedef {import('../../types').GiftOptionsData} GiftOptionsData
 * @typedef {import('../../types').AssociatePinsData} AssociatePinsData
 * @typedef {import('../../types').ClientAccountDetails} ClientAccountDetails
 *
 * @typedef {Object} ProductMetaData
 * @property {number} [itemQty]
 * @property {AdjustedPriceData} [adjustedPriceInfo]
 * @property {GiftOptionsData} [giftOptions]
 * @property {AssociatePinsData[]} [associatePins]
 *
 * @typedef {Object} CartContextGetters
 * @property {ProductDTO[]} productsInCartData
 * @property {Map<string, ProductMetaData>} productsMetaData
 * @property {ProfileData} profileData
 * @property {string} selectedAccountId
 * @property {ShippingAddressDTO[]} shippingAddresses
 * @property {BillingAddressDTO[]} billingAddresses
 * @property {ClientAccountDetails} clientAccountDetails
 *
 * @typedef {Object} CartContextSetters
 * @property {React.Dispatch<React.SetStateAction<ProductDTO[]>>} setProductsInCartData
 * @property {React.Dispatch<React.SetStateAction<Map<string, ProductMetaData>>} setProductsMetaData
 * @property {React.Dispatch<React.SetStateAction<ProfileData>>} setProfileData
 * @property {React.Dispatch<React.SetStateAction<string>>} setSelectedAccountId
 * @property {React.Dispatch<React.SetStateAction<ShippingAddressDTO[]>>} setShippingAddresses
 * @property {React.Dispatch<React.SetStateAction<BillingAddressDTO[]>>} setBillingAddresses
 * @property {React.Dispatch<React.SetStateAction<ClientAccountDetails>>} setClientAccountDetails
 *
 * @typedef {Object} InitialState
 * @property {ProductDTO[]} products
 * @property {Map<string, ProductMetaData>} productsMetaData
 * @property {ProfileData} profileData
 * @property {string} selectedAccountId
 * @property {ShippingAddressDTO[]} shippingAddresses
 * @property {ClientAccountDetails} clientAccountDetails
 */

/** @type {React.Context<CartContextGetters>} */
export const CartContextGetters = createContext({});
CartContextGetters.displayName = 'CartContextGetters';

/** @type {React.Context<CartContextSetters>} */
export const CartContextSetters = createContext({});
CartContextSetters.displayName = 'CartContextSetters';

/** @type {ClientAccountDetails} */
const initialClientAccountDetails = {
  payments: [],
  shippingAddress: [],
  billingAddress: [],
  email: '',
  firstName: '',
  lastName: '',
  profileId: '',
};

export const CartProvider = ({ children }) => {
  /** @type {InitialState} */
  const initialState = {
    products: [],
    productsMetaData: new Map(),
    profileData: {},
    selectedAccountId: '',
    shippingAddresses: [],
    billingAddresses: [],
    clientAccountDetails: initialClientAccountDetails,
  };
  const [productsInCartData, setProductsInCartData] = React.useState(initialState.products);
  const [productsMetaData, setProductsMetaData] = React.useState(initialState.productsMetaData);
  const [profileData, setProfileData] = React.useState(initialState.profileData);
  const [selectedAccountId, setSelectedAccountId] = React.useState(initialState.selectedAccountId);
  const [shippingAddresses, setShippingAddresses] = React.useState(initialState.shippingAddresses);
  const [billingAddresses, setBillingAddresses] = React.useState(initialState.billingAddresses);
  const [clientAccountDetails, setClientAccountDetails] = React.useState(initialState.clientAccountDetails);

  const getters = {
    productsInCartData,
    productsMetaData,
    profileData,
    selectedAccountId,
    shippingAddresses,
    billingAddresses,
    clientAccountDetails,
  };

  const setters = {
    setProductsInCartData,
    setProductsMetaData,
    setProfileData,
    setSelectedAccountId,
    setShippingAddresses,
    setBillingAddresses,
    setClientAccountDetails,
  };

  return (
    <CartContextGetters.Provider value={{ ...getters }}>
      <CartContextSetters.Provider value={{ ...setters }}>{children}</CartContextSetters.Provider>
    </CartContextGetters.Provider>
  );
};

export const useCartContext = () => {
  const getters = useContext(CartContextGetters);
  const setters = useContext(CartContextSetters);

  if (getters === undefined || setters === undefined) {
    throw new Error('useCartContext must be used within CartProvider');
  }

  return Object.assign({}, getters, setters);
};
